/**
 * Portal-aware label utilities
 *
 * Labels change based on whether the user is viewing as a Customer or Supplier.
 * This matches the old portal behavior where dashboard labels were context-sensitive.
 *
 * Customer Portal labels focus on purchasing/spend:
 *   - "Open Order Value" - value of orders to suppliers
 *   - "Total Spend" - money spent purchasing goods
 *   - "Suppliers" - companies selling to you
 *
 * Supplier Portal labels focus on sales/revenue:
 *   - "Open Sales Order Value" - value of orders from customers
 *   - "Total Revenue" - money received from customers
 *   - "Customers" - companies buying from you
 */

type PortalType = 'CUSTOMER' | 'SUPPLIER' | 'HELIX' | 'customer' | 'supplier' | 'helix' | 'customer_manager';

interface PortalLabels {
  // Dashboard KPI labels
  openOrderValue: string;
  totalSpend: string;
  historicalArrearsValue: string;

  // Entity labels
  supplierSingular: string;
  supplierPlural: string;
  customerSingular: string;
  customerPlural: string;

  // Section headers
  suppliersRequiringAttention: string;

  // Chart titles
  spendByMonth: string;

  // Ranking labels
  worstQuality: string;
  worstOTIF: string;
  worstOTD: string;
  highestArrears: string;

  // Navigation labels
  ordersMenuLabel: string;
  ordersPageTitle: string;
}

const CUSTOMER_LABELS: PortalLabels = {
  // Dashboard KPIs
  openOrderValue: 'OPEN ORDER VALUE',
  totalSpend: 'TOTAL SPEND',
  historicalArrearsValue: 'HISTORICAL ARREARS VALUE',

  // Entities
  supplierSingular: 'Supplier',
  supplierPlural: 'Suppliers',
  customerSingular: 'Customer',
  customerPlural: 'Customers',

  // Sections
  suppliersRequiringAttention: 'Suppliers Requiring Attention',

  // Charts
  spendByMonth: 'Spend by Month',

  // Rankings
  worstQuality: 'Worst Quality (PPM)',
  worstOTIF: 'Worst OTIF Performance',
  worstOTD: 'Worst On-Time Delivery',
  highestArrears: 'Highest Arrears',

  // Navigation
  ordersMenuLabel: 'Purchase Orders',
  ordersPageTitle: 'Purchase Orders',
};

const SUPPLIER_LABELS: PortalLabels = {
  // Dashboard KPIs - renamed to sales/revenue focus
  openOrderValue: 'OPEN SALES ORDER VALUE',
  totalSpend: 'TOTAL REVENUE',
  historicalArrearsValue: 'HISTORICAL BACKLOG VALUE',

  // Entities - swapped perspective
  supplierSingular: 'Supplier',  // Still called supplier, but context is different
  supplierPlural: 'Suppliers',
  customerSingular: 'Customer',
  customerPlural: 'Customers',

  // Sections - from supplier's perspective, focusing on customer relationships
  suppliersRequiringAttention: 'Customers Requiring Attention',

  // Charts
  spendByMonth: 'Revenue by Month',

  // Rankings - from supplier's perspective
  worstQuality: 'Quality Issues by Customer',
  worstOTIF: 'OTIF Issues by Customer',
  worstOTD: 'OTD Issues by Customer',
  highestArrears: 'Highest Backlog by Customer',

  // Navigation
  ordersMenuLabel: 'Sales Orders',
  ordersPageTitle: 'Sales Orders',
};

/**
 * Customer Portal mode labels — supplier is platform owner, customers are guests.
 * From the supplier-owner's perspective, "Suppliers" label becomes "Customers"
 * because they're managing their customers, not other suppliers.
 */
const CUSTOMER_PORTAL_LABELS: PortalLabels = {
  openOrderValue: 'OPEN ORDER VALUE',
  totalSpend: 'TOTAL REVENUE',
  historicalArrearsValue: 'HISTORICAL BACKLOG VALUE',

  supplierSingular: 'Customer',
  supplierPlural: 'Customers',
  customerSingular: 'Supplier',
  customerPlural: 'Suppliers',

  suppliersRequiringAttention: 'Customers Requiring Attention',
  spendByMonth: 'Revenue by Month',

  worstQuality: 'Worst Quality by Customer',
  worstOTIF: 'Worst OTIF by Customer',
  worstOTD: 'Worst OTD by Customer',
  highestArrears: 'Highest Backlog by Customer',

  ordersMenuLabel: 'Customer Orders',
  ordersPageTitle: 'Customer Orders',
};

/**
 * Get portal-specific labels based on user's platform
 */
export function getPortalLabels(platform: PortalType | undefined): PortalLabels {
  const normalized = platform?.toUpperCase();
  if (normalized === 'SUPPLIER') {
    return SUPPLIER_LABELS;
  }
  if (platform === 'customer_manager') {
    return CUSTOMER_PORTAL_LABELS;
  }
  // Default to customer labels (for CUSTOMER, HELIX, and undefined)
  return CUSTOMER_LABELS;
}

/**
 * Get a specific label based on portal type
 */
export function getPortalLabel(
  platform: PortalType | undefined,
  key: keyof PortalLabels
): string {
  const labels = getPortalLabels(platform);
  return labels[key];
}

/**
 * React hook for getting portal-aware labels
 * To be used with useAuth() context
 */
export function usePortalLabels(platform: PortalType | undefined) {
  return getPortalLabels(platform);
}

/**
 * Check if current view is supplier portal
 */
export function isSupplierPortal(platform: PortalType | undefined): boolean {
  return platform?.toUpperCase() === 'SUPPLIER';
}

/**
 * Check if current view is customer portal
 */
export function isCustomerPortal(platform: PortalType | undefined): boolean {
  const normalized = platform?.toUpperCase();
  return normalized === 'CUSTOMER' || normalized === 'HELIX' || platform === undefined;
}

export type { PortalType, PortalLabels };
